import React, { createContext, useCallback, useContext, useState } from "react";
import { postExportList } from "../../services/exportListService";
import { getErrorMessage } from "../../locales/translationUtils";
import LoadingOverlay from "../../components/Loading";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { getReportListaCargo } from "../../services/cargoService";
import logo from "../../assets/GiantLog-logo.png";
import i18n from "../../i18n";

interface ExportListContextProps {
  loading: boolean;
  error: string | null;
  exportList: (type: string, filters: any) => void;
}

interface ExportListProviderProps {
  children: React.ReactNode;
}

const ExportListContext = createContext<ExportListContextProps | null>(null);

const generatePDF = async (filters: any) => {
  try {
    filters.paginated = false;
    const response = await getReportListaCargo(filters);
    
    const doc = new jsPDF({
      orientation: "landscape",
      unit: "mm",
      format: "a4",
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 15;

    const header = () => {
      doc.addImage(logo, "PNG", margin, 10, 26.46, 11.73); // x, y, width, height
      doc.setFontSize(16);
      doc.text(filters.situation, pageWidth / 2, 20, { align: "center" });
    };

    const footer = (data: any) => {
      const str = `Página ${data.pageNumber}`;
      const dateStr = new Date().toLocaleString("pt-BR");
      doc.setFontSize(10);
      doc.text(str, margin, doc.internal.pageSize.getHeight() - 10);
      doc.text(dateStr, pageWidth - margin, doc.internal.pageSize.getHeight() - 10, {
        align: "right",
      });
    };

    autoTable(doc, {
      startY: 25,
      head: [[
        i18n.t('report.fields.fromIdentification'), 
        i18n.t('report.fields.clientName'), 
        i18n.t('report.fields.supplierName'), 
        i18n.t('report.fields.dawb'), 
        i18n.t('report.fields.pickUpDateFormatted'), 
        i18n.t('report.fields.arrivalDateFormatted'),
        i18n.t('report.fields.inputDateFormatted'), 
        i18n.t('report.fields.poNumber'), 
        i18n.t('report.fields.wrNumber'), 
        i18n.t('report.fields.invoice'), 
        i18n.t('report.fields.weight'), 
        i18n.t('report.fields.pieces'), 
        i18n.t('report.fields.shippedBy'),
        i18n.t('report.fields.destination'),
        i18n.t('report.fields.to'),
        i18n.t('report.fields.releasedBy'),
        i18n.t('report.fields.releasedFormatted'),
        i18n.t('report.fields.remarks')
      ]],
      body: response.data.values.map((item) => [
        item.fromIdentification, 
        item.clientName, 
        item.supplierName, 
        item.dawb, 
        item.puFormatted, 
        item.arrivalFormatted,
        item.inputDateFormatted, 
        item.poNumber,
        item.wrNumber, 
        item.invoice, 
        item.weightRaw ?? item.weight, 
        item.pieces, 
        item.shippedBy,
        item.destination,
        item.to,
        item.releasedBy,
        item.releasedFormatted,
        item.description
      ]),
      didDrawPage: (data) => {
        header();
        footer(data);
      },
      styles: {
        fontSize: 7,
        cellPadding: 1.5,
        overflow: 'linebreak',
        cellWidth: 'wrap',
        halign: 'left',
        valign: 'middle',
      },
      headStyles: {
        fillColor: [131, 53, 42],
        textColor: 255,
        fontSize: 7,
        fontStyle: 'bold',
        halign: 'center',
        valign: 'middle',
      },
      columnStyles: {
        0: { cellWidth: 12 },  // From
        1: { cellWidth: 18 },  // Client
        2: { cellWidth: 22 },  // Supplier
        3: { cellWidth: 15 },  // Dawb
        4: { cellWidth: 16 },  // Pick up date
        5: { cellWidth: 16 },  // Arrival Date
        6: { cellWidth: 16 },  // Input date
        7: { cellWidth: 14 },  // Po Number
        8: { cellWidth: 14 },  // Wr Number
        9: { cellWidth: 14 },  // Invoice
        10: { cellWidth: 13 }, // Weight
        11: { cellWidth: 12 }, // Pieces
        12: { cellWidth: 16 }, // Shipped By
        13: { cellWidth: 19 }, // Destination
        14: { cellWidth: 13 }, // To
        15: { cellWidth: 16 }, // Released By
        16: { cellWidth: 15 }, // Release
        17: { cellWidth: 'auto' }, // Remarks
      },
      alternateRowStyles: {
        fillColor: [235, 235, 235],
      },
      margin: { top: 25, left: 10, right: 10 },
      tableWidth: 'auto',
    });

    doc.save("relatorio.pdf");
  } catch (err) {
    console.error("Erro ao gerar PDF:", err);
  }
}

const ExportListProvider: React.FC<ExportListProviderProps> = ({ children }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const exportList = useCallback(async (type: string, filters: any) => {
    setError(null);
    
    try {
      setLoading(true);

      if (["csv", "xlsx"].includes(type)) {
        filters.paginated = false;
        const response = await postExportList(type, filters);
  
        const downloadUrl = window.URL.createObjectURL(response.data);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = `relatorio.${type}`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(downloadUrl);
      }
      else {
        await generatePDF(filters);
      }
    } catch (err) {
      setError(getErrorMessage('fetchExportListError'));
    } finally {
      setLoading(false);
    }
  }, []);

  return (
    <ExportListContext.Provider value={{ loading, error, exportList }}>
      <LoadingOverlay isLoading={loading} />
      {children}
    </ExportListContext.Provider>
  );
};

export default ExportListProvider;

export const useExportList = () => {
  const context = useContext(ExportListContext);
  if (!context) {
    throw new Error(getErrorMessage("wrongUseProviderError", 
      { 
        auth: useExportList?.name, 
        providerName: ExportListProvider?.name
      }
    ));
  }
  return context;
};